import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Hexagon, Loader2 } from "lucide-react";
import Orb from "@/components/Orb";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Auth() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/hub");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grid-bg">
      {/* left visual */}
      <div className="hidden lg:flex relative items-center justify-center glow-radial border-r border-blue-900/30">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative z-10 text-center px-10">
          <Orb state="idle" size={200} />
          <h2 className="font-display text-3xl font-bold mt-10">The System awaits</h2>
          <p className="text-slate-400 mt-3 max-w-sm mx-auto">
            Sign in to begin your Awakening. ARCHON will read your build and unlock your personal System.
          </p>
        </div>
      </div>

      {/* form */}
      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-3 mb-8 cursor-pointer" onClick={() => navigate("/")}>
            <Hexagon className="text-cyan-400" size={22} />
            <span className="font-display text-xl font-bold tracking-[0.3em]">ARCHON</span>
          </div>

          <p className="mono-label">{mode === "login" ? "IDENTITY VERIFICATION" : "REGISTER NEW PRINCIPAL"}</p>
          <h1 className="font-display text-3xl font-bold mt-2 mb-8">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <input
                data-testid="auth-name-input"
                placeholder="Name"
                value={name} onChange={(e) => setName(e.target.value)} required
                className="w-full bg-slate-950/50 border border-blue-900/50 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none text-slate-50 rounded-lg px-4 py-3 transition-all"
              />
            )}
            <input
              data-testid="auth-email-input"
              type="email" placeholder="Email"
              value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-slate-950/50 border border-blue-900/50 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none text-slate-50 rounded-lg px-4 py-3 transition-all"
            />
            <input
              data-testid="auth-password-input"
              type="password" placeholder="Password"
              value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full bg-slate-950/50 border border-blue-900/50 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 outline-none text-slate-50 rounded-lg px-4 py-3 transition-all"
            />

            {error && <p data-testid="auth-error" className="text-red-400 text-sm">{error}</p>}

            <button
              data-testid="auth-submit-btn"
              type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white py-3 rounded-lg font-medium transition-all duration-300 hover:shadow-[0_0_24px_rgba(59,130,246,0.45)]"
            >
              {busy && <Loader2 className="animate-spin" size={18} />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <p className="text-slate-400 text-sm mt-6">
            {mode === "login" ? "New here?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-btn"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-cyan-400 hover:underline"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>

          <div className="mt-8 text-xs text-slate-500 font-mono border border-blue-900/30 rounded-lg p-3">
            The System selects those with potential. Sign in to begin your Awakening.
          </div>
        </motion.div>
      </div>
    </div>
  );
}
